/**
 * ProjectDetailPage - displays project overview with boards
 * Supports multiple view modes for boards: grid, list, timeline, table
 */

import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { clsx } from 'clsx';
import {
  Plus,
  LayoutGrid,
  Settings,
  Users,
  BarChart3,
  FolderKanban,
  MoreHorizontal,
  Pencil,
  Trash2,
  X,
  Archive,
  ArchiveRestore,
} from 'lucide-react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import * as Dialog from '@radix-ui/react-dialog';
import { useProjectStore } from '@stores/projectStore';
import { useBoardStore } from '@stores/boardStore';
import { boardsApi, type Board } from '@services/api';
import LoadingSpinner from '@components/ui/LoadingSpinner';
import { toast } from '@components/ui/Toast';
import { ProjectTeamModal, ProjectSettingsModal } from '@components/projects';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import {
  GenericViewSwitcher,
  ProjectTasksView,
  ProjectFilesView,
  type GenericViewType,
} from '@/components/shared/views';
import {
  IconPicker,
  ColorPicker,
  colorOptions,
  isEmoji,
  getLucideIcon,
} from '@/components/shared/IconPicker';

// Schema for editing board
const editBoardSchema = z.object({
  name: z
    .string()
    .min(1, 'Board name is required')
    .max(255, 'Board name must be less than 255 characters'),
  description: z
    .string()
    .max(500, 'Description must be less than 500 characters')
    .optional(),
});

type EditBoardFormData = z.infer<typeof editBoardSchema>;

interface BoardForEdit {
  id: string;
  name: string;
  description?: string;
  color?: string;
  icon?: string;
}

export default function ProjectDetailPage() {
  const { t } = useTranslation();
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();

  const {
    currentProject,
    projectStats,
    isLoading: projectLoading,
    fetchProject,
    fetchProjectStats,
  } = useProjectStore();

  const {
    boards,
    isLoading: boardsLoading,
    fetchBoards,
    createBoard,
    updateBoard,
    deleteBoard,
    archiveBoard,
    restoreBoard,
  } = useBoardStore();

  const [showCreateBoardDialog, setShowCreateBoardDialog] = useState(false);
  const [boardsViewType, setBoardsViewType] = useState<GenericViewType>('grid');

  // Modal states
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);

  // Board edit/delete states
  const [editingBoard, setEditingBoard] = useState<BoardForEdit | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingBoardId, setDeletingBoardId] = useState<string | null>(null);

  // Archived boards live outside the store's `boards` (which stays "active only"
  // so the all-tasks views keep ignoring them); they are fetched on demand.
  const [showArchived, setShowArchived] = useState(false);
  const [archivedBoards, setArchivedBoards] = useState<Board[]>([]);
  const [archivedCount, setArchivedCount] = useState(0);

  const loadArchivedBoards = async (pid: string) => {
    const response = await boardsApi.list(pid, true);
    const archived = response.items.filter((b) => b.isArchived);
    setArchivedBoards(archived);
    setArchivedCount(archived.length);
  };

  useEffect(() => {
    if (projectId) {
      void loadArchivedBoards(projectId).catch(() => undefined);
    }
  }, [projectId]);

  const handleArchiveBoard = async (boardId: string) => {
    if (!projectId) return;
    try {
      await archiveBoard(boardId);
      toast.success(t('board.boardArchived'));
      await Promise.all([fetchBoards(projectId), loadArchivedBoards(projectId)]);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('board.failedArchiveBoard'));
    }
  };

  const handleRestoreBoard = async (boardId: string) => {
    if (!projectId) return;
    try {
      await restoreBoard(boardId);
      toast.success(t('board.boardRestored'));
      await Promise.all([fetchBoards(projectId), loadArchivedBoards(projectId)]);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('board.failedRestoreBoard'));
    }
  };

  const visibleBoards: Board[] = showArchived ? [...boards, ...archivedBoards] : boards;

  useEffect(() => {
    if (projectId) {
      fetchProject(projectId);
      fetchProjectStats(projectId);
      fetchBoards(projectId);
    }
  }, [projectId, fetchProject, fetchProjectStats, fetchBoards]);

  const handleCreateBoard = async (data: {
    name: string;
    description?: string;
    icon?: string;
    color?: string;
  }) => {
    if (!projectId) return;

    const createData: {
      projectId: string;
      name: string;
      description?: string;
      icon?: string;
      color?: string;
    } = {
      projectId,
      name: data.name,
    };
    if (data.description) {
      createData.description = data.description;
    }
    if (data.icon) {
      createData.icon = data.icon;
    }
    if (data.color) {
      createData.color = data.color;
    }
    const board = await createBoard(createData);
    setShowCreateBoardDialog(false);
    navigate(`/boards/${board.id}`);
  };

  if (projectLoading && !currentProject) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-neutral-50 dark:bg-neutral-900">
        <LoadingSpinner size="lg" className="text-primary-600" />
      </div>
    );
  }

  if (!currentProject) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-neutral-50 dark:bg-neutral-900">
        <p className="text-neutral-500">{t('project.notFound')}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900">
      {/* Header */}
      <header className="border-b border-neutral-200 bg-white px-6 py-6 dark:border-neutral-800 dark:bg-neutral-900">
        <div className="mx-auto max-w-7xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {(() => {
                const iconValue = currentProject.icon || 'folder-kanban';
                const isEmojiIcon = isEmoji(iconValue);
                const ProjectIcon = !isEmojiIcon ? getLucideIcon(iconValue) || FolderKanban : null;
                return (
                  <div
                    className={clsx(
                      'flex h-12 w-12 items-center justify-center rounded-xl text-white',
                      !currentProject.color && 'bg-primary-600'
                    )}
                    style={
                      currentProject.color
                        ? { backgroundColor: currentProject.color }
                        : undefined
                    }
                  >
                    {isEmojiIcon ? (
                      <span className="text-2xl">{iconValue}</span>
                    ) : ProjectIcon ? (
                      <ProjectIcon className="h-6 w-6" />
                    ) : (
                      <FolderKanban className="h-6 w-6" />
                    )}
                  </div>
                );
              })()}
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">
                    {currentProject.name}
                  </h1>
                  <span className="rounded bg-neutral-100 px-2 py-0.5 text-sm font-medium text-neutral-500 dark:bg-neutral-800">
                    {currentProject.key}
                  </span>
                </div>
                {currentProject.description && (
                  <p className="mt-1 text-neutral-500 dark:text-neutral-400">
                    {currentProject.description}
                  </p>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowTeamModal(true)}
                className={clsx(
                  'flex items-center gap-2 rounded-lg px-3 py-2',
                  'text-neutral-600 hover:bg-neutral-100',
                  'dark:text-neutral-400 dark:hover:bg-neutral-800'
                )}
              >
                <Users className="h-4 w-4" />
                {t('project.team')}
              </button>
              <button
                onClick={() => setShowSettingsModal(true)}
                className={clsx(
                  'flex items-center gap-2 rounded-lg px-3 py-2',
                  'text-neutral-600 hover:bg-neutral-100',
                  'dark:text-neutral-400 dark:hover:bg-neutral-800'
                )}
              >
                <Settings className="h-4 w-4" />
                {t('project.settings')}
              </button>
            </div>
          </div>

          {/* Stats */}
          {projectStats && (
            <div className="mt-6 grid grid-cols-4 gap-4">
              <StatCard
                label={t('project.totalTasks')}
                value={projectStats.taskCount}
                icon={LayoutGrid}
              />
              <StatCard
                label={t('project.completed')}
                value={projectStats.completedTaskCount}
                icon={BarChart3}
                color="text-green-500"
              />
              <StatCard
                label={t('project.overdue')}
                value={projectStats.overdueTaskCount}
                icon={BarChart3}
                color="text-red-500"
              />
              <StatCard
                label={t('project.members')}
                value={projectStats.memberCount}
                icon={Users}
              />
            </div>
          )}
        </div>
      </header>

      {/* Boards section */}
      <main className="mx-auto max-w-7xl px-6 py-8">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-neutral-900 dark:text-white">
            {t('project.boards')}
          </h2>
          <div className="flex items-center gap-4">
            {archivedCount > 0 && boardsViewType === 'grid' && (
              <button
                type="button"
                onClick={() => setShowArchived((v) => !v)}
                aria-pressed={showArchived}
                className={clsx(
                  'flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-medium transition-colors',
                  showArchived
                    ? 'border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-300'
                    : 'border-neutral-200 text-neutral-500 hover:bg-neutral-50 dark:border-neutral-700 dark:text-neutral-400 dark:hover:bg-neutral-800'
                )}
                title={showArchived ? t('project.hideArchivedBoards') : t('project.showArchivedBoards')}
              >
                <Archive className="h-3.5 w-3.5" />
                {t('project.archivedBoards', { count: archivedCount })}
              </button>
            )}
            <GenericViewSwitcher
              currentView={boardsViewType}
              onViewChange={setBoardsViewType}
            />
            <button
              onClick={() => setShowCreateBoardDialog(true)}
              className={clsx(
                'flex items-center gap-2 rounded-lg px-3 py-2',
                'bg-primary-600 text-white hover:bg-primary-700'
              )}
            >
              <Plus className="h-4 w-4" />
              {t('project.newBoard')}
            </button>
          </div>
        </div>

        {/* Boards content based on view type */}
        <div className="mt-6">
          {boardsLoading && boards.length === 0 ? (
            <div className="flex justify-center py-8">
              <LoadingSpinner size="md" className="text-neutral-400" />
            </div>
          ) : boards.length === 0 ? (
            <div className="rounded-xl border border-neutral-200 bg-white p-12 text-center dark:border-neutral-800 dark:bg-neutral-900">
              <LayoutGrid className="mx-auto h-12 w-12 text-neutral-300 dark:text-neutral-600" />
              <h3 className="mt-4 text-lg font-medium text-neutral-900 dark:text-white">
                {t('project.noBoards')}
              </h3>
              <p className="mt-2 text-neutral-500 dark:text-neutral-400">
                {t('project.noBoardsDescription')}
              </p>
              <button
                onClick={() => setShowCreateBoardDialog(true)}
                className={clsx(
                  'mt-4 inline-flex items-center gap-2 rounded-lg px-4 py-2',
                  'bg-primary-600 text-white hover:bg-primary-700'
                )}
              >
                <Plus className="h-4 w-4" />
                {t('project.createBoard')}
              </button>
            </div>
          ) : boardsViewType === 'grid' ? (
            // Grid View (original card layout)
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {visibleBoards.map((board) => (
                <Link
                  key={board.id}
                  to={`/boards/${board.id}`}
                  className={clsx(
                    'group rounded-xl border border-neutral-200 bg-white p-5',
                    'transition-shadow hover:shadow-lg',
                    'dark:border-neutral-800 dark:bg-neutral-900',
                    board.isArchived && 'border-dashed opacity-70 hover:opacity-100'
                  )}
                >
                  <div className="flex items-start justify-between">
                    {(() => {
                      const iconValue = board.icon || 'layout-grid';
                      const isEmojiIcon = isEmoji(iconValue);
                      const BoardIcon = !isEmojiIcon ? getLucideIcon(iconValue) || LayoutGrid : null;
                      return (
                        <div
                          className="flex h-10 w-10 items-center justify-center rounded-lg text-white"
                          style={{ backgroundColor: board.color || '#3B82F6' }}
                        >
                          {isEmojiIcon ? (
                            <span className="text-xl">{iconValue}</span>
                          ) : BoardIcon ? (
                            <BoardIcon className="h-5 w-5" />
                          ) : (
                            <LayoutGrid className="h-5 w-5" />
                          )}
                        </div>
                      );
                    })()}
                    <DropdownMenu.Root>
                      <DropdownMenu.Trigger asChild>
                        <button
                          onClick={(e) => e.preventDefault()}
                          className={clsx(
                            'rounded p-1 opacity-0 transition-opacity group-hover:opacity-100',
                            'text-neutral-400 hover:bg-neutral-100 hover:text-neutral-600',
                            'dark:hover:bg-neutral-800 dark:hover:text-neutral-300'
                          )}
                        >
                          <MoreHorizontal className="h-4 w-4" />
                        </button>
                      </DropdownMenu.Trigger>
                      <DropdownMenu.Portal>
                        <DropdownMenu.Content
                          className={clsx(
                            'z-50 min-w-[160px] rounded-lg border border-neutral-200 bg-white p-1 shadow-lg',
                            'dark:border-neutral-700 dark:bg-neutral-800'
                          )}
                          align="end"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <DropdownMenu.Item
                            onSelect={() => setEditingBoard(board)}
                            className={clsx(
                              'flex cursor-pointer items-center gap-2 rounded px-3 py-2 text-sm',
                              'text-neutral-700 hover:bg-neutral-100',
                              'dark:text-neutral-300 dark:hover:bg-neutral-700'
                            )}
                          >
                            <Pencil className="h-4 w-4" />
                            {t('common.edit')}
                          </DropdownMenu.Item>
                          {board.isArchived ? (
                            <DropdownMenu.Item
                              onSelect={() => void handleRestoreBoard(board.id)}
                              className={clsx(
                                'flex cursor-pointer items-center gap-2 rounded px-3 py-2 text-sm',
                                'text-primary-700 hover:bg-primary-50',
                                'dark:text-primary-300 dark:hover:bg-primary-900/20'
                              )}
                            >
                              <ArchiveRestore className="h-4 w-4" />
                              {t('board.restoreBoard')}
                            </DropdownMenu.Item>
                          ) : (
                            <DropdownMenu.Item
                              onSelect={() => void handleArchiveBoard(board.id)}
                              className={clsx(
                                'flex cursor-pointer items-center gap-2 rounded px-3 py-2 text-sm',
                                'text-amber-700 hover:bg-amber-50',
                                'dark:text-amber-400 dark:hover:bg-amber-900/20'
                              )}
                            >
                              <Archive className="h-4 w-4" />
                              {t('board.archiveBoard')}
                            </DropdownMenu.Item>
                          )}
                          <DropdownMenu.Item
                            onSelect={() => {
                              setDeletingBoardId(board.id);
                              setShowDeleteConfirm(true);
                            }}
                            className={clsx(
                              'flex cursor-pointer items-center gap-2 rounded px-3 py-2 text-sm',
                              'text-red-600 hover:bg-red-50',
                              'dark:text-red-400 dark:hover:bg-red-900/20'
                            )}
                          >
                            <Trash2 className="h-4 w-4" />
                            {t('common.delete')}
                          </DropdownMenu.Item>
                        </DropdownMenu.Content>
                      </DropdownMenu.Portal>
                    </DropdownMenu.Root>
                  </div>
                  <h3 className="mt-4 font-semibold text-neutral-900 dark:text-white">
                    {board.name}
                  </h3>
                  {board.description && (
                    <p className="mt-1 line-clamp-2 text-sm text-neutral-500 dark:text-neutral-400">
                      {board.description}
                    </p>
                  )}
                  <div className="mt-4 flex flex-col gap-2 text-sm text-neutral-500 dark:text-neutral-400">
                    <div className="flex items-center gap-4">
                      <span>{t('project.columnsCount', { count: board.columns.length })}</span>
                      <span>{t('project.tasksCount', { count: board.taskCount ?? 0 })}</span>
                      {board.isDefault && (
                        <span className="rounded bg-primary-100 px-2 py-0.5 text-xs font-medium text-primary-600 dark:bg-primary-900/30 dark:text-primary-400">
                          {t('project.defaultBadge')}
                        </span>
                      )}
                      {board.isArchived && (
                        <span className="inline-flex items-center gap-1 rounded bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                          <Archive className="h-3 w-3" />
                          {t('board.archivedBadge')}
                        </span>
                      )}
                    </div>
                    {board.columnTaskCounts && board.columns.length > 0 && (
                      <div className="flex flex-wrap gap-1.5">
                        {board.columns.map((col) => (
                          <span
                            key={col.id}
                            className="inline-flex items-center gap-1 rounded bg-neutral-100 px-1.5 py-0.5 text-xs dark:bg-neutral-800"
                          >
                            <span
                              className="h-2 w-2 rounded-full"
                              style={{ backgroundColor: col.color || '#6B7280' }}
                            />
                            <span className="max-w-[80px] truncate">{col.name}</span>
                            <span className="font-medium">
                              {board.columnTaskCounts?.[col.id] ?? 0}
                            </span>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          ) : boardsViewType === 'files' ? (
            // Files View - Show ALL files across all boards in this project
            <ProjectFilesView projectId={projectId || ''} />
          ) : (
            // List/Timeline/Table Views - Show ALL tasks across all boards in this project
            <ProjectTasksView
              projectId={projectId || ''}
              viewMode={boardsViewType === 'list' ? 'list' : boardsViewType === 'timeline' ? 'timeline' : 'table'}
            />
          )}
        </div>
      </main>

      {/* Team Modal */}
      <ProjectTeamModal
        projectId={projectId || ''}
        organizationId={currentProject?.organizationId || ''}
        open={showTeamModal}
        onClose={() => setShowTeamModal(false)}
      />

      {/* Settings Modal */}
      <ProjectSettingsModal
        project={currentProject}
        open={showSettingsModal}
        onClose={() => setShowSettingsModal(false)}
        onDelete={() => navigate('/projects')}
      />

      {/* Delete board confirmation dialog */}
      <ConfirmDialog
        open={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setDeletingBoardId(null);
        }}
        onConfirm={async () => {
          if (deletingBoardId) {
            await deleteBoard(deletingBoardId);
            setShowDeleteConfirm(false);
            setDeletingBoardId(null);
          }
        }}
        title={t('project.deleteBoardTitle')}
        description={t('project.deleteBoardDescription')}
        confirmLabel={t('common.delete')}
        variant="danger"
      />

      {/* Edit board dialog */}
      <EditBoardDialog
        board={editingBoard}
        open={!!editingBoard}
        onClose={() => setEditingBoard(null)}
        onSave={async (data) => {
          if (editingBoard) {
            await updateBoard(editingBoard.id, data);
            setEditingBoard(null);
          }
        }}
      />

      {/* Create board dialog */}
      <CreateBoardDialog
        open={showCreateBoardDialog}
        onClose={() => setShowCreateBoardDialog(false)}
        onSave={handleCreateBoard}
      />
    </div>
  );
}

interface StatCardProps {
  label: string;
  value: number;
  icon: React.ElementType;
  color?: string;
}

function StatCard({ label, value, icon: Icon, color }: StatCardProps) {
  return (
    <div className="rounded-xl border border-neutral-200 bg-white p-4 dark:border-neutral-800 dark:bg-neutral-900">
      <div className="flex items-center gap-3">
        <Icon className={clsx('h-5 w-5', color || 'text-neutral-400')} />
        <div>
          <p className="text-2xl font-bold text-neutral-900 dark:text-white">
            {value}
          </p>
          <p className="text-sm text-neutral-500 dark:text-neutral-400">
            {label}
          </p>
        </div>
      </div>
    </div>
  );
}

// Edit Board Dialog Component
interface EditBoardDialogProps {
  board: BoardForEdit | null;
  open: boolean;
  onClose: () => void;
  onSave: (data: { name: string; description?: string; color?: string; icon?: string }) => Promise<void>;
}

function EditBoardDialog({ board, open, onClose, onSave }: EditBoardDialogProps) {
  const { t } = useTranslation();
  const [isSaving, setIsSaving] = useState(false);
  const [selectedColor, setSelectedColor] = useState(colorOptions[0]);
  const [selectedIcon, setSelectedIcon] = useState<string>('layout-grid');

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<EditBoardFormData>({
    resolver: zodResolver(editBoardSchema),
  });

  // Reset form when board changes
  useEffect(() => {
    if (board) {
      reset({
        name: board.name,
        description: board.description || '',
      });
      setSelectedColor(board.color || colorOptions[0]);
      setSelectedIcon(board.icon || 'layout-grid');
    }
  }, [board, reset]);

  const onSubmit = async (data: EditBoardFormData) => {
    setIsSaving(true);
    try {
      const saveData: { name: string; description?: string; color?: string; icon?: string } = {
        name: data.name,
      };
      if (data.description) {
        saveData.description = data.description;
      }
      if (selectedColor) {
        saveData.color = selectedColor;
      }
      if (selectedIcon) {
        saveData.icon = selectedIcon;
      }
      await onSave(saveData);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <Dialog.Content
          className={clsx(
            'fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2',
            'w-full max-w-xl rounded-xl bg-white p-6 shadow-xl',
            'dark:bg-neutral-900 dark:border dark:border-neutral-800',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
            'data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%]',
            'data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]'
          )}
        >
          <div className="flex items-center justify-between">
            <Dialog.Title className="text-lg font-semibold text-neutral-900 dark:text-white">
              {t('project.editBoard')}
            </Dialog.Title>
            <Dialog.Close asChild>
              <button
                className={clsx(
                  'rounded-lg p-1.5',
                  'text-neutral-400 hover:bg-neutral-100 hover:text-neutral-600',
                  'dark:hover:bg-neutral-800 dark:hover:text-neutral-300'
                )}
              >
                <X className="h-5 w-5" />
              </button>
            </Dialog.Close>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="mt-4 space-y-5">
            {/* Icon Picker */}
            <div>
              <p className="mb-2 text-sm font-medium text-neutral-700 dark:text-neutral-300">
                {t('project.boardIcon')}
              </p>
              <IconPicker
                value={selectedIcon}
                onChange={setSelectedIcon}
                color={selectedColor}
              />
            </div>

            {/* Color Picker */}
            <div>
              <p className="mb-2 text-sm font-medium text-neutral-700 dark:text-neutral-300">
                {t('project.iconColor')}
              </p>
              <ColorPicker value={selectedColor} onChange={setSelectedColor} />
            </div>

            {/* Board name */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300">
                {t('project.boardNameRequired')}
              </label>
              <input
                {...register('name')}
                type="text"
                className={clsx(
                  'mt-1 w-full rounded-lg border px-4 py-2.5',
                  'bg-white text-neutral-900 dark:bg-neutral-800 dark:text-white',
                  errors.name
                    ? 'border-red-500'
                    : 'border-neutral-300 dark:border-neutral-700',
                  'focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500'
                )}
                placeholder={t('project.boardNamePlaceholder')}
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-500">{errors.name.message}</p>
              )}
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300">
                {t('project.boardDescription')}
              </label>
              <textarea
                {...register('description')}
                rows={3}
                className={clsx(
                  'mt-1 w-full rounded-lg border px-4 py-2.5',
                  'bg-white text-neutral-900 dark:bg-neutral-800 dark:text-white',
                  'border-neutral-300 dark:border-neutral-700',
                  'focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500'
                )}
                placeholder={t('project.boardDescPlaceholder')}
              />
              {errors.description && (
                <p className="mt-1 text-sm text-red-500">{errors.description.message}</p>
              )}
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 border-t border-neutral-200 pt-4 dark:border-neutral-800">
              <button
                type="button"
                onClick={onClose}
                className={clsx(
                  'rounded-lg px-4 py-2 text-sm font-medium',
                  'text-neutral-700 hover:bg-neutral-100',
                  'dark:text-neutral-300 dark:hover:bg-neutral-800'
                )}
              >
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                disabled={isSaving}
                className={clsx(
                  'rounded-lg px-4 py-2 text-sm font-medium',
                  'bg-primary-600 text-white hover:bg-primary-700',
                  'disabled:cursor-not-allowed disabled:opacity-50'
                )}
              >
                {isSaving ? t('common.saving') : t('common.saveChanges')}
              </button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

// Create Board Dialog Component
interface CreateBoardDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: { name: string; description?: string; color?: string; icon?: string }) => Promise<void>;
}

function CreateBoardDialog({ open, onClose, onSave }: CreateBoardDialogProps) {
  const { t } = useTranslation();
  const [isSaving, setIsSaving] = useState(false);
  const [selectedColor, setSelectedColor] = useState(colorOptions[0]);
  const [selectedIcon, setSelectedIcon] = useState<string>('layout-grid');

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<EditBoardFormData>({
    resolver: zodResolver(editBoardSchema),
    defaultValues: {
      name: '',
      description: '',
    },
  });

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      reset({
        name: '',
        description: '',
      });
      setSelectedColor(colorOptions[0]);
      setSelectedIcon('layout-grid');
    }
  }, [open, reset]);

  const onSubmit = async (data: EditBoardFormData) => {
    setIsSaving(true);
    try {
      const saveData: { name: string; description?: string; color?: string; icon?: string } = {
        name: data.name,
      };
      if (data.description) {
        saveData.description = data.description;
      }
      if (selectedColor) {
        saveData.color = selectedColor;
      }
      if (selectedIcon) {
        saveData.icon = selectedIcon;
      }
      await onSave(saveData);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <Dialog.Content
          className={clsx(
            'fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2',
            'w-full max-w-xl rounded-xl bg-white p-6 shadow-xl',
            'dark:bg-neutral-900 dark:border dark:border-neutral-800',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
            'data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%]',
            'data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]'
          )}
        >
          <div className="flex items-center justify-between">
            <Dialog.Title className="text-lg font-semibold text-neutral-900 dark:text-white">
              {t('project.createNewBoard')}
            </Dialog.Title>
            <Dialog.Close asChild>
              <button
                className={clsx(
                  'rounded-lg p-1.5',
                  'text-neutral-400 hover:bg-neutral-100 hover:text-neutral-600',
                  'dark:hover:bg-neutral-800 dark:hover:text-neutral-300'
                )}
              >
                <X className="h-5 w-5" />
              </button>
            </Dialog.Close>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="mt-4 space-y-5">
            {/* Icon Picker */}
            <div>
              <p className="mb-2 text-sm font-medium text-neutral-700 dark:text-neutral-300">
                {t('project.boardIcon')}
              </p>
              <IconPicker
                value={selectedIcon}
                onChange={setSelectedIcon}
                color={selectedColor}
              />
            </div>

            {/* Color Picker */}
            <div>
              <p className="mb-2 text-sm font-medium text-neutral-700 dark:text-neutral-300">
                {t('project.iconColor')}
              </p>
              <ColorPicker value={selectedColor} onChange={setSelectedColor} />
            </div>

            {/* Board name */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300">
                {t('project.boardNameRequired')}
              </label>
              <input
                {...register('name')}
                type="text"
                className={clsx(
                  'mt-1 w-full rounded-lg border px-4 py-2.5',
                  'bg-white text-neutral-900 dark:bg-neutral-800 dark:text-white',
                  errors.name
                    ? 'border-red-500'
                    : 'border-neutral-300 dark:border-neutral-700',
                  'focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500'
                )}
                placeholder={t('project.boardNamePlaceholder')}
                autoFocus
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-500">{errors.name.message}</p>
              )}
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300">
                {t('project.boardDescription')}
              </label>
              <textarea
                {...register('description')}
                rows={3}
                className={clsx(
                  'mt-1 w-full rounded-lg border px-4 py-2.5',
                  'bg-white text-neutral-900 dark:bg-neutral-800 dark:text-white',
                  'border-neutral-300 dark:border-neutral-700',
                  'focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500'
                )}
                placeholder={t('project.boardDescPlaceholder')}
              />
              {errors.description && (
                <p className="mt-1 text-sm text-red-500">{errors.description.message}</p>
              )}
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 border-t border-neutral-200 pt-4 dark:border-neutral-800">
              <button
                type="button"
                onClick={onClose}
                className={clsx(
                  'rounded-lg px-4 py-2 text-sm font-medium',
                  'text-neutral-700 hover:bg-neutral-100',
                  'dark:text-neutral-300 dark:hover:bg-neutral-800'
                )}
              >
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                disabled={isSaving}
                className={clsx(
                  'rounded-lg px-4 py-2 text-sm font-medium',
                  'bg-primary-600 text-white hover:bg-primary-700',
                  'disabled:cursor-not-allowed disabled:opacity-50'
                )}
              >
                {isSaving ? t('common.creating') : t('project.createBoard')}
              </button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
